import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, ArrowUpRight, Trash2, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { listPilots, createPilot, deletePilot } from "@/lib/api";

const emptyForm = {
  slug: "",
  name: "",
  industry: "",
  city: "",
  tagline: "",
  hero_headline: "",
  hero_subheadline: "",
  services: "",
  phone: "",
  email: "",
  address: "",
  accent_color: "#c9a24a",
  alfred_variant: "auto", // 'auto' | 'work' | 'business_casual' | 'business'
  logo_url: "",
  trial_days: 30,
  active: true,
};

export default function AdminPilots() {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listPilots();
      setPilots(data);
    } catch {
      toast.error("Failed to load pilots.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.name || !form.industry) {
      toast.error("Slug, name, and industry are required.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        services: form.services
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean),
        email: form.email || undefined,
        phone: form.phone || undefined,
        city: form.city || undefined,
        tagline: form.tagline || undefined,
        hero_headline: form.hero_headline || undefined,
        hero_subheadline: form.hero_subheadline || undefined,
        address: form.address || undefined,
        logo_url: form.logo_url || undefined,
        accent_color: form.accent_color || undefined,
        alfred_variant: form.alfred_variant && form.alfred_variant !== "auto" ? form.alfred_variant : undefined,
        trial_days: Number(form.trial_days) || 30,
      };
      await createPilot(payload);
      toast.success(`Pilot /${form.slug} is live.`);
      setForm(emptyForm);
      setShowForm(false);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create pilot.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete pilot /${slug} and all its leads?`)) return;
    try {
      await deletePilot(slug);
      toast.success(`Deleted /${slug}`);
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <main
      className="min-h-screen w-full bg-[#0a0a0b] text-white/90"
      data-testid="admin-pilots-page"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 py-14 md:py-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div>
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
              <Link to="/" className="hover:text-white/80 transition-colors">
                OLS
              </Link>
              <span className="mx-2 text-white/20">/</span>
              <span>Admin</span>
              <span className="mx-2 text-white/20">/</span>
              <span className="text-white/70">Pilots</span>
            </div>
            <h1
              className="font-serif-display text-white text-4xl md:text-5xl leading-tight"
              data-testid="admin-heading"
            >
              Pilot programs.
            </h1>
            <p className="font-mono-plex text-sm text-white/45 mt-3 max-w-xl">
              30-day trials for local businesses. Spin one up and share the URL —{" "}
              <code className="text-white/70">ourlocalstories.com/&lt;slug&gt;</code>.
            </p>
          </div>

          <Button
            onClick={() => setShowForm((v) => !v)}
            data-testid="admin-toggle-create"
            className="rounded-none h-12 px-6 bg-white text-black hover:bg-white/85 font-mono-plex text-[11px] uppercase tracking-[0.24em] font-medium inline-flex items-center gap-2 self-start sm:self-auto"
          >
            <Plus className="h-3.5 w-3.5" />
            {showForm ? "Close" : "New Pilot"}
          </Button>
        </div>

        {/* Create form */}
        {showForm && (
          <form
            onSubmit={handleCreate}
            className="mb-14 border border-white/15 bg-white/[0.02] p-6 sm:p-8"
            data-testid="admin-create-form"
          >
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-6">
              New pilot configuration
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AdminInput
                label="Slug (URL path)"
                required
                placeholder="marion-auto"
                value={form.slug}
                onChange={(v) => setForm({ ...form, slug: v.toLowerCase() })}
                testId="admin-input-slug"
                hint="lowercase, hyphens only — will live at /<slug>"
              />
              <AdminInput
                label="Business name"
                required
                placeholder="Marion Auto"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testId="admin-input-name"
              />
              <AdminInput
                label="Industry"
                required
                placeholder="Automotive Service"
                value={form.industry}
                onChange={(v) => setForm({ ...form, industry: v })}
                testId="admin-input-industry"
              />
              <AdminInput
                label="City"
                placeholder="Marion, OH"
                value={form.city}
                onChange={(v) => setForm({ ...form, city: v })}
                testId="admin-input-city"
              />
              <AdminInput
                label="Hero headline"
                placeholder="The garage Marion trusts — now online."
                value={form.hero_headline}
                onChange={(v) => setForm({ ...form, hero_headline: v })}
                testId="admin-input-headline"
              />
              <AdminInput
                label="Hero subheadline"
                placeholder="Describe what your car is doing…"
                value={form.hero_subheadline}
                onChange={(v) => setForm({ ...form, hero_subheadline: v })}
                testId="admin-input-subheadline"
              />
              <AdminInput
                label="Tagline"
                placeholder="Straight talk. Honest wrenching."
                value={form.tagline}
                onChange={(v) => setForm({ ...form, tagline: v })}
                testId="admin-input-tagline"
              />
              <AdminInput
                label="Logo URL"
                placeholder="https://..."
                value={form.logo_url}
                onChange={(v) => setForm({ ...form, logo_url: v })}
                testId="admin-input-logo"
              />
              <AdminInput
                label="Phone"
                placeholder="(740) 555-0142"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testId="admin-input-phone"
              />
              <AdminInput
                label="Email"
                placeholder="shop@marionauto.com"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testId="admin-input-email"
              />
              <AdminInput
                label="Address"
                placeholder="148 W Center St, Marion, OH"
                value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                testId="admin-input-address"
              />
              <div className="flex items-end gap-4">
                <AdminInput
                  label="Accent hex"
                  placeholder="#c9a24a"
                  value={form.accent_color}
                  onChange={(v) => setForm({ ...form, accent_color: v })}
                  testId="admin-input-accent"
                  className="flex-1"
                />
                <div
                  className="h-12 w-12 border border-white/15"
                  style={{ background: form.accent_color || "transparent" }}
                  aria-label="Accent color preview"
                />
              </div>
            </div>

            {/* Alfred variant selector */}
            <div className="mt-6">
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40 block mb-3">
                Alfred variant
                <span className="ml-2 text-white/25 normal-case tracking-normal font-sans-inter">
                  · leave on Auto to derive from industry
                </span>
              </label>
              <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3"
                data-testid="admin-alfred-variant-picker"
              >
                {[
                  { key: "auto", label: "Auto (detect)", src: null },
                  { key: "work", label: "Work polo", src: "/alfred/work.png" },
                  { key: "business_casual", label: "Business casual", src: "/alfred/business_casual.png" },
                  { key: "business", label: "Business suit", src: "/alfred/business.png" },
                ].map((opt) => {
                  const selected = form.alfred_variant === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setForm({ ...form, alfred_variant: opt.key })}
                      data-testid={`admin-alfred-variant-${opt.key}`}
                      className={`flex flex-col items-center gap-2 border p-3 transition-colors ${
                        selected
                          ? "border-white/70 bg-white/[0.04]"
                          : "border-white/15 bg-white/[0.02] hover:border-white/30"
                      }`}
                    >
                      {opt.src ? (
                        <img
                          src={opt.src}
                          alt={opt.label}
                          className="h-16 w-16 rounded-full object-cover border border-white/10"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-full border border-dashed border-white/20 flex items-center justify-center font-mono-plex text-[10px] uppercase tracking-[0.24em] text-white/40">
                          Auto
                        </div>
                      )}
                      <span className="font-mono-plex text-[10px] uppercase tracking-[0.22em] text-white/70">
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <label className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40 block mb-2">
                Services (one per line or comma-separated)
              </label>
              <Textarea
                value={form.services}
                onChange={(e) => setForm({ ...form, services: e.target.value })}
                placeholder={"Diagnostics\nOil & Fluid Service\nBrake Repair"}
                rows={4}
                data-testid="admin-input-services"
                className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide px-4 py-3 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50 resize-none"
              />
            </div>

            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="admin-input-active"
                />
                <span className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/60">
                  Active
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40">
                  Trial days
                </span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={form.trial_days}
                  onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                  data-testid="admin-input-trial-days"
                  className="w-20 h-9 bg-white/[0.02] border border-white/15 text-white font-mono-plex text-sm px-3 focus:outline-none focus:border-white/50"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <Button
                type="submit"
                disabled={creating}
                data-testid="admin-create-submit"
                className="rounded-none h-11 px-5 bg-white text-black hover:bg-white/85 font-mono-plex text-[11px] uppercase tracking-[0.24em] font-medium inline-flex items-center gap-2 disabled:opacity-70"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    Create pilot <ArrowUpRight className="h-3.5 w-3.5" />
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setForm(emptyForm);
                }}
                className="font-mono-plex text-[11px] uppercase tracking-[0.28em] text-white/45 hover:text-white/85 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Pilot list */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40">
              {loading ? "Loading…" : `${pilots.length} pilot${pilots.length === 1 ? "" : "s"}`}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 font-mono-plex text-[11px] text-white/40">
              <Loader2 className="h-4 w-4 animate-spin" /> Fetching pilots…
            </div>
          ) : pilots.length === 0 ? (
            <div
              className="border border-dashed border-white/15 p-12 text-center"
              data-testid="admin-empty-state"
            >
              <p className="font-serif-display text-white/80 text-2xl mb-3">No pilots yet.</p>
              <p className="font-mono-plex text-[12px] text-white/45">
                Create your first pilot to get started.
              </p>
            </div>
          ) : (
            <ul className="border-t border-white/10" data-testid="admin-pilot-list">
              {pilots.map((p) => (
                <li
                  key={p.id}
                  className="border-b border-white/10 py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
                  data-testid={`admin-pilot-row-${p.slug}`}
                >
                  <div
                    className="h-2 w-2 shrink-0"
                    style={{ background: p.accent_color || "#c9a24a" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-serif-display text-white text-xl">{p.name}</span>
                      <code className="font-mono-plex text-[11px] text-white/50">/{p.slug}</code>
                      <span className="font-mono-plex text-[10px] uppercase tracking-[0.24em] text-white/35">
                        {p.industry}
                      </span>
                    </div>
                    <div className="mt-1 font-mono-plex text-[11px] text-white/40 flex items-center gap-4 flex-wrap">
                      <span>
                        {p.trial_days_remaining > 0
                          ? `${p.trial_days_remaining} of ${p.trial_days} days left`
                          : "Trial complete"}
                      </span>
                      <span
                        className={
                          p.active ? "text-green-400/70" : "text-white/30"
                        }
                      >
                        {p.active ? "● live" : "○ paused"}
                      </span>
                      {p.city && <span>· {p.city}</span>}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Link
                      to={`/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="h-9 px-3 border border-white/15 text-white/70 hover:bg-white hover:text-black font-mono-plex text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-2 transition-colors"
                      data-testid={`admin-view-${p.slug}`}
                    >
                      View <ExternalLink className="h-3 w-3" />
                    </Link>
                    <Link
                      to={`/admin/pilots/${p.slug}`}
                      className="h-9 px-3 border border-white/15 text-white/70 hover:bg-white hover:text-black font-mono-plex text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-2 transition-colors"
                      data-testid={`admin-manage-${p.slug}`}
                    >
                      Leads
                    </Link>
                    <button
                      onClick={() => handleDelete(p.slug)}
                      className="h-9 w-9 border border-white/15 text-white/50 hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 inline-flex items-center justify-center transition-colors"
                      data-testid={`admin-delete-${p.slug}`}
                      aria-label={`Delete ${p.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

const AdminInput = ({ label, value, onChange, placeholder, required, testId, hint, className = "" }) => (
  <div className={className}>
    <label className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40 block mb-2">
      {label}
      {required && <span className="text-white/70 ml-1">*</span>}
    </label>
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="rounded-none border-white/15 bg-white/[0.02] text-white placeholder:text-white/30 font-mono-plex text-sm tracking-wide h-12 px-4 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/50"
    />
    {hint && <div className="mt-1 font-mono-plex text-[10px] text-white/30">{hint}</div>}
  </div>
);
